import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Sun, Moon, ChevronDown, KeyRound, LogOut, Menu, Sparkles, UserCircle,
  Megaphone, ArrowUpRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../theme/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import GlobalSearch from './GlobalSearch'
import NotificationsBell from '../components/notifications/NotificationsBell'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { groups, type Accent } from './navItems'
import { MKT_SSO_URL, canOpenMktTool } from '../lib/marketing'

/**
 * El título de la barra sale del MISMO sitio que el menú lateral: navItems.ts.
 *
 * Antes había aquí una lista paralela ruta→título, y se desincronizó tres veces:
 * Pagos recurrentes ponía "Ajustes", Tesorería ponía "JR2 OS", y las once
 * pantallas de Contabilidad y las nueve de Ajustes ponían el nombre del MÓDULO
 * PADRE en lugar del suyo — el menú resaltaba "Modelos AEAT" y la cabecera decía
 * "Contabilidad". Derivándolo del árbol de navegación no pueden discrepar, y una
 * pantalla nueva aparece titulada sin tocar este fichero.
 */
const NAV_ITEMS = groups
  .flatMap((g) => g.items)
  // Un enlace externo se lleva al usuario fuera de la SPA: nunca llega a
  // renderizar este layout, así que no puede titular ninguna pantalla.
  .filter((i) => !i.external && i.to.startsWith('/'))

/** El acento del menú, traducido a las clases de gradiente que existen en CSS. */
const ACCENT_GRADIENT: Record<Accent, string> = {
  toga: 'gradient-text-toga',
  cyan: 'gradient-text-cyan',
  purple: 'gradient-text-purple',
  pink: 'gradient-text-rose',
  orange: 'gradient-text-orange',
  rose: 'gradient-text-rose',
  emerald: 'gradient-text-green',
  indigo: 'gradient-text-blue',
  amber: 'gradient-text-orange',
  sky: 'gradient-text-blue',
  blue: 'gradient-text-blue',
  fuchsia: 'gradient-text-purple',
}

/**
 * Rutas que NO son entradas del menú y aun así necesitan título. Hoy solo la
 * portada de Ajustes: sus hijas sí están en el menú, pero /settings a secas no.
 * Las fichas de detalle (/billing/:id, /clients/:id…) no hacen falta aquí — el
 * emparejado por prefijo les da el título de su listado, que es lo que se
 * espera al abrir un registro desde él.
 */
const EXTRA: Record<string, { key: string; gradient: string }> = {
  '/settings': { key: 'pageTitle.settings', gradient: 't-primary' },
}

/**
 * Rutas heredadas que hoy renderizan OTRA pantalla. Los catálogos se fundieron
 * en uno solo, pero las direcciones antiguas siguen vivas para no romper
 * enlaces guardados; se titulan como la pantalla que realmente muestran, no
 * como "Ajustes".
 */
const ALIAS: Record<string, string> = {
  '/settings/project-roles': '/settings/catalogs',
  '/settings/products': '/settings/catalogs',
  '/settings/service-lines': '/settings/catalogs',
  '/settings/expense-categories': '/settings/catalogs',
  '/settings/expense-rules': '/settings/catalogs',
  '/settings/advisors': '/settings/catalogs',
}


function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

interface Props {
  onChangePassword: () => void
  onOpenSidebar?: () => void
}

export default function Topbar({ onChangePassword, onOpenSidebar }: Props) {
  const { pathname } = useLocation()
  const { theme, toggle } = useTheme()
  const auth = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  const isLight = theme === 'light'
  // Gana la ruta MÁS LARGA que sea prefijo de la actual, de forma que
  // /accounting/aeat se titula "Modelos AEAT" y no "Contabilidad", mientras que
  // una ficha sin entrada propia (/billing/:id) hereda el nombre de su listado.
  //
  // Se compara por SEGMENTOS (`=== to` o empieza por `to + '/'`) y no por texto
  // suelto, para que una futura /rates-x no se cuele bajo /rates.
  const route = ALIAS[pathname] ?? pathname
  const navMatch = NAV_ITEMS.filter(
    (i) => route === i.to || route.startsWith(`${i.to}/`)
  ).sort((a, b) => b.to.length - a.to.length)[0]

  const extraKey = Object.keys(EXTRA)
    .filter((k) => route === k || route.startsWith(`${k}/`))
    .sort((a, b) => b.length - a.length)[0]

  // El menú manda; EXTRA solo cubre lo que no es una entrada del menú.
  const titleEntry = navMatch
    ? // El mismo `t(labelKey, label)` que usa el Sidebar, así que aunque falte
      // la traducción los dos caen al mismo literal por defecto.
      { label: t(navMatch.labelKey, navMatch.label), gradient: ACCENT_GRADIENT[navMatch.accent] }
    : extraKey
      ? { label: t(EXTRA[extraKey]!.key), gradient: EXTRA[extraKey]!.gradient }
      : // Solo debería verse en el parpadeo de "/" antes de redirigir.
        { label: 'JR2 OS', gradient: 't-primary' }

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    if (menuOpen) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  const user = auth.status === 'authenticated' ? auth.user : null

  const logout = async () => {
    await auth.logout()
    navigate('/login', { replace: true })
  }

  return (
    <header
      className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 md:px-8 backdrop-blur border-b"
      style={{ background: 'var(--bg-topbar)', borderColor: 'var(--border)' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {onOpenSidebar && (
          <button
            type="button"
            onClick={onOpenSidebar}
            className={[
              'md:hidden w-9 h-9 grid place-items-center rounded-lg transition',
              isLight ? 'hover:bg-slate-200/60 text-slate-700' : 'hover:bg-slate-800/60 text-slate-700 dark:text-slate-200',
            ].join(' ')}
            aria-label={t('topbar.openMenu')}
          >
            <Menu size={20} />
          </button>
        )}
        <h1 className={`text-base md:text-xl font-bold ${titleEntry.gradient} truncate`}>{titleEntry.label}</h1>
      </div>
      <div className="flex items-center gap-2 md:gap-3">
        <div className="hidden md:block">
          <GlobalSearch isLight={isLight} />
        </div>
        {/* JR2 MKT, la herramienta de marketing de HOY: una aplicación aparte
            (mkt.jr2.es) a la que se entra por SSO. Estaba en el menú lateral y
            se muda aquí porque esa entrada pasa a ser la herramienta propia que
            se construye dentro de JR2 OS. Es un <a> y no un navigate: la
            navegación tiene que ir al backend, que firma el token.

            `hidden md:grid`, el mismo envoltorio que el buscador de al lado:
            en móvil esta fila ya va justa y un sexto icono dejaba el título de
            la pantalla en puros puntos suspensivos —y el título es la única
            pista de dónde estás cuando el menú lateral está cerrado—. No se
            pierde el acceso: en móvil la entrada es Menú → Marketing, que trae
            su propio botón «Abrir JR2 MKT». */}
        {canOpenMktTool(user?.role) && (
          <a
            href={MKT_SSO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={[
              'relative w-9 h-9 hidden md:grid place-items-center rounded-full transition',
              isLight
                ? 'hover:bg-fuchsia-100/60 text-fuchsia-600'
                : 'hover:bg-fuchsia-500/15 text-fuchsia-300',
            ].join(' ')}
            aria-label={t('topbar.mktTool')}
            title={t('topbar.mktTool')}
          >
            <Megaphone size={18} />
            {/* Marca de «se abre fuera»: dos megáfonos (este y el del menú) sin
                distinguir serían dos destinos distintos con el mismo icono. */}
            <ArrowUpRight size={10} className="absolute top-1.5 right-1.5 opacity-80" />
          </a>
        )}
        <button
          type="button"
          onClick={() => navigate('/ai')}
          className={[
            'w-9 h-9 grid place-items-center rounded-full transition',
            isLight
              ? 'hover:bg-purple-100/60 text-purple-600'
              : 'hover:bg-purple-500/15 text-purple-300',
          ].join(' ')}
          aria-label={t('topbar.aiAssistant')}
          title={t('topbar.aiAssistant')}
        >
          <Sparkles size={18} />
        </button>
        <LanguageSwitcher isLight={isLight} />
        <button
          type="button"
          onClick={toggle}
          className={[
            'w-9 h-9 grid place-items-center rounded-full transition',
            isLight
              ? 'hover:bg-slate-200/60 text-indigo-500'
              : 'hover:bg-slate-800/60 text-amber-300',
          ].join(' ')}
          aria-label={t('topbar.toggleTheme')}
        >
          {isLight ? <Moon size={18} /> : <Sun size={18} />}
        </button>
        <NotificationsBell isLight={isLight} />

        {user && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={[
                'flex items-center gap-2 pl-1 pr-2 py-1 rounded-full transition',
                isLight ? 'hover:bg-slate-200/60' : 'hover:bg-slate-800/60',
              ].join(' ')}
            >
              <span className="w-8 h-8 rounded-full grid place-items-center bg-gradient-to-br from-toga-600 to-toga-800 text-white text-xs font-semibold">
                {initials(user.fullName)}
              </span>
              <ChevronDown size={14} className={isLight ? 't-muted' : 'text-slate-400'} />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-64 surface-deep p-2 shadow-xl animate-slideUp"
                style={{ background: 'var(--bg-surface-strong)' }}
              >
                <div className="px-3 py-2 border-b border-base mb-1">
                  <div className="text-sm font-semibold t-primary truncate">{user.fullName}</div>
                  <div className="text-xs t-muted truncate">{user.email}</div>
                  <div className="text-[11px] uppercase tracking-wider text-toga-300 mt-1">
                    {t('enums:Role.' + user.role)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    navigate('/settings')
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm t-secondary hover:bg-hover"
                >
                  <UserCircle size={16} /> {t('topbar.myProfile')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    onChangePassword()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm t-secondary hover:bg-hover"
                >
                  <KeyRound size={16} /> {t('topbar.changePassword')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    void logout()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-rose-400 hover:bg-rose-500/10"
                >
                  <LogOut size={16} /> {t('topbar.logout')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
